import { GlobalState } from "@ict/shared";
import { t } from "@/i18n/i18n";
import { EVENTS, type AppSocket } from "@/network/SocketClient";
import type { ClockSync } from "@/network/ClockSync";
import { CeremonyUI } from "@/ui/ceremonyUI";
import { VideoPreloader } from "@/video/VideoPreloader";
import { VideoSyncController } from "@/video/VideoSyncController";
import type { AudioManager } from "@/audio/AudioManager";

export interface CeremonyHost {
  /** Stop the render loop and free scene GPU resources so the video plays smoothly (spec section 21). */
  suspendGameEngine(): void;
}

const VIDEO_SRC = (import.meta.env.VITE_VIDEO_PATH as string | undefined) ?? "/video/opening.mp4";

/**
 * Orchestrates the ceremony sequence client-side. Deliberately talks to the
 * game engine only through the narrow CeremonyHost interface: a 3D engine
 * failure must never be able to prevent the ceremony from playing.
 */
export class CeremonyController {
  readonly ui: CeremonyUI;
  readonly preloader: VideoPreloader;
  readonly videoSync: VideoSyncController;

  private countdownHandle: number | null = null;
  private currentSchedule: { ceremonyStartAt: number } | null = null;

  constructor(
    private readonly socket: AppSocket,
    private readonly clockSync: ClockSync,
    private readonly host: CeremonyHost,
    private readonly audio: AudioManager,
    container: HTMLElement
  ) {
    this.ui = new CeremonyUI(container);
    this.preloader = new VideoPreloader(this.ui.videoEl);
    this.preloader.load(VIDEO_SRC);
    this.videoSync = new VideoSyncController(this.ui.videoEl, this.clockSync);

    this.ui.videoEl.addEventListener("ended", () => {
      this.socket.emit(EVENTS.CEREMONY_VIDEO_ENDED);
    });

    this.socket.on(EVENTS.CEREMONY_PREPARE, () => this.onPrepare());
    this.socket.on(EVENTS.CEREMONY_SCHEDULED, (payload) => this.onScheduled(payload.ceremonyStartAt));
    this.socket.on(EVENTS.CEREMONY_START, () => this.onStart());
    this.socket.on(EVENTS.CEREMONY_SYNC, (payload) => {
      this.currentSchedule = { ceremonyStartAt: payload.ceremonyStartAt };
    });
    this.socket.on(EVENTS.CEREMONY_END, () => this.onEnd());
  }

  private onPrepare(): void {
    this.host.suspendGameEngine();
    // Stop looping ambient so it doesn't bleed under the countdown, but keep
    // the SFX bus at full volume — the countdown beep still needs to be
    // audible here. Game audio is only fully killed right before the video
    // itself starts, in onStart() (see spec section 26: "ก่อน Video เริ่ม").
    this.audio.stopAll();
    this.ui.showLayer();
    this.ui.setText(t("ceremony.preparing"));
  }

  private onScheduled(ceremonyStartAt: number): void {
    this.currentSchedule = { ceremonyStartAt };
    this.ui.showLayer();
    this.beginCountdown(ceremonyStartAt);
  }

  private beginCountdown(ceremonyStartAt: number): void {
    this.clearCountdown();
    let lastSecond: number | null = null;
    const tick = () => {
      const msLeft = ceremonyStartAt - this.clockSync.serverNow();
      const secondsLeft = Math.ceil(msLeft / 1000);
      if (msLeft <= 0) {
        this.ui.fadeToBlack(true);
        this.clearCountdown();
        return;
      }
      if (secondsLeft !== lastSecond) {
        lastSecond = secondsLeft;
        if (secondsLeft <= 5) {
          this.ui.setText(String(secondsLeft), true);
          this.audio.play("countdown");
        } else {
          this.ui.setText(t("ceremony.opening"));
        }
      }
      this.countdownHandle = window.setTimeout(tick, 100);
    };
    tick();
  }

  private clearCountdown(): void {
    if (this.countdownHandle !== null) {
      window.clearTimeout(this.countdownHandle);
      this.countdownHandle = null;
    }
  }

  private async onStart(): Promise<void> {
    this.clearCountdown();
    this.audio.fadeOutAndStop(400);
    const startAt = this.currentSchedule?.ceremonyStartAt ?? this.clockSync.serverNow();
    this.ui.showVideo();
    const played = await this.videoSync.startAt(startAt);
    this.ui.fadeToBlack(false);
    if (!played) {
      // Autoplay was blocked; surface a tap-to-continue affordance.
      this.ui.setText(t("ceremony.tapToStart"));
      this.ui.showLayer();
      const resume = async () => {
        const ok = await this.videoSync.retryPlay();
        if (ok) {
          this.ui.hideText();
          this.ui.hideLayer();
          window.removeEventListener("click", resume);
        }
      };
      window.addEventListener("click", resume, { once: true });
    }
  }

  private onEnd(): void {
    this.videoSync.stop();
  }

  /** Called after JOINED, in case we connected mid-ceremony (reconnect/late join). */
  resumeFromState(state: GlobalState, ceremonyStartAt: number | null): void {
    if (!ceremonyStartAt) return;
    this.currentSchedule = { ceremonyStartAt };
    if (state === GlobalState.CEREMONY_COUNTDOWN) {
      this.host.suspendGameEngine();
      this.audio.stopAll();
      this.ui.showLayer();
      this.beginCountdown(ceremonyStartAt);
    } else if (state === GlobalState.CEREMONY_PLAYING) {
      this.host.suspendGameEngine();
      this.onStart();
    } else if (state === GlobalState.CEREMONY_PREPARING) {
      this.onPrepare();
    }
  }

  getDebugInfo() {
    return this.videoSync.getDebugInfo();
  }
}
